<template>
  <div
    v-if="shouldShow"
    class="global-float-btn"
    :style="btnStyle"
    @pointerdown="onPointerDown"
  >
    <a-tooltip placement="topRight" title="常见问题，AI智能解答">
      <div
        class="global-float-cont global-float-top"
        @click.stop="handleAiAssistantClick"
      >
        <img
          :src="global1"
          alt="Smiley face"
          width="32"
          height="32"
          style="pointer-events: none"
        />
        <div>AI助手</div>
      </div>
    </a-tooltip>

    <a-tooltip
      placement="topRight"
      title="轻松打字，语音对话就可以完成下单操作"
    >
      <div class="global-float-cont" @click.stop="handleCloudChatClick">
        <img
          :src="global2"
          alt="Smiley face"
          width="32"
          height="32"
          style="pointer-events: none"
        />
        <div>云对话</div>
      </div>
    </a-tooltip>
  </div>

  <a-drawer
    v-model:visible="containerDrawerVisible"
    size="large"
    class="custom-class"
    title="常见问题，AI智能解答"
    placement="right"
    :body-style="{ padding: '0px' }"
  >
    <iframe
      v-if="containerDrawerVisible"
      src="https://chatbot.weixin.qq.com/webapp/0Gu0Rk1Hn3qEYRjraKt5JNnBCmoQHi?isFloat=false&robotName=%E8%BD%BB%E9%A3%9F%E6%98%93%E5%AE%89%E6%99%BA%E8%83%BD%E5%AE%A2%E6%9C%8D"
      frameborder="0"
      allowfullscreen="true"
      webkitallowfullscreen="true"
      mozallowfullscreen="true"
      style="position: absolute; width: 100%; height: 90%"
    ></iframe>
  </a-drawer>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import global1 from "@/assets/image/global-1.png";
import global2 from "@/assets/image/global-2.png";
import { message } from "ant-design-vue";
import { useRoute } from "vue-router";
import { useStore } from "@/store/store";

// const { createMessage } = useMessage();
const route = useRoute();
const store = useStore();

let containerDrawerVisible = ref(false);

// 配置需要隐藏 GlobalFloatButton 的路由路径
// const hiddenRoutes = [
//   "/login",
//   // 在这里添加更多需要隐藏的路径
// ];

// 悬浮按钮总开关：读取本地保存的扫码收货配置中的 floatEnabled，默认开启
const floatEnabled = computed(() => {
  const saved = store?.getScanReceivingConfig;
  const parsed =
    saved && typeof saved === "string" ? JSON.parse(saved) : saved || {};
  return parsed.floatEnabled === undefined ? true : !!parsed.floatEnabled;
});

// 判断当前路由是否应该显示 GlobalFloatButton
const shouldShow = computed(() => {
  // console.log(route.path, "route.path");
  // console.log(route.name, "route.path");
  // return !hiddenRoutes.some((path) => route.path.startsWith(path));
  return route.path !== "/" && floatEnabled.value;
});

// 拖拽相关状态
const isDragging = ref(false);
// 悬浮按钮位置本地存储 key
const FLOAT_POS_KEY = "global_float_btn_position";

// 读取上次保存的位置，没有则使用默认位置
function loadSavedPosition() {
  try {
    const saved = localStorage.getItem(FLOAT_POS_KEY);
    if (saved) {
      const pos = JSON.parse(saved);
      if (typeof pos.x === "number" && typeof pos.y === "number") {
        return { x: pos.x, y: pos.y };
      }
    }
  } catch (e) {
    // 忽略解析错误，使用默认位置
  }
  return {
    x: window.innerWidth - 86,
    y: window.innerHeight - 245,
  };
}

const btnPosition = ref(loadSavedPosition());
const dragStart = ref({ x: 0, y: 0 });
const startPos = ref({ x: 0, y: 0 });

// 保存当前位置到本地存储
function savePosition() {
  try {
    localStorage.setItem(
      FLOAT_POS_KEY,
      JSON.stringify({
        x: btnPosition.value.x,
        y: btnPosition.value.y,
      })
    );
  } catch (e) {
    // 忽略存储失败（如隐私模式）
  }
}

// 动态样式
const btnStyle = computed(() => ({
  left: `${btnPosition.value.x}px`,
  top: `${btnPosition.value.y}px`,
}));

// 指针按下开始拖拽（同时兼容鼠标与触屏）
function onPointerDown(e: PointerEvent) {
  // 鼠标仅响应左键，触屏/笔忽略按键判断
  if (e.pointerType === "mouse" && e.button !== 0) return;
  isDragging.value = true;
  dragStart.value = { x: e.clientX, y: e.clientY };
  startPos.value = { ...btnPosition.value };
  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerUp);
  document.addEventListener("pointercancel", onPointerUp);
}

// 指针移动
function onPointerMove(e: PointerEvent) {
  if (!isDragging.value) return;
  const dx = e.clientX - dragStart.value.x;
  const dy = e.clientY - dragStart.value.y;
  btnPosition.value = {
    x: startPos.value.x + dx,
    y: startPos.value.y + dy,
  };
}

// 指针松开结束拖拽
function onPointerUp(e: PointerEvent) {
  isDragging.value = false;
  document.removeEventListener("pointermove", onPointerMove);
  document.removeEventListener("pointerup", onPointerUp);
  document.removeEventListener("pointercancel", onPointerUp);

  // 吸附到屏幕边缘
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  const btnWidth = 56;
  const btnHeight = 56;

  // 水平方向吸附
  if (btnPosition.value.x < screenWidth / 2) {
    btnPosition.value.x = 20;
  } else {
    btnPosition.value.x = screenWidth - btnWidth - 20;
  }

  // 垂直方向限制
  if (btnPosition.value.y < 20) {
    btnPosition.value.y = 20;
  } else if (btnPosition.value.y > screenHeight - btnHeight - 20) {
    btnPosition.value.y = screenHeight - btnHeight - 20;
  }

  // 拖拽结束后保存位置，下次进入直接恢复到该位置
  savePosition();
}

// AI助手点击事件
function handleAiAssistantClick() {
  containerDrawerVisible.value = true;
}

// 云对话点击事件
function handleCloudChatClick() {
  message.warning(`云对话正在开发中！`);
  // createMessage.warn('云对话正在开发中');
}
</script>

<style lang="less" scoped>
.global-float-btn {
  position: fixed;
  width: 50px;
  height: 160px;
  border-radius: 30px;
  background: #fff;
  color: #16b76a;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px #16b76a99;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  z-index: 1000;
  user-select: none;
  text-align: center;
  touch-action: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.6);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
  }

  .global-float-top {
    position: relative;
    padding-bottom: 8px;
    margin-bottom: 8px;

    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 80%;
      height: 1px;
      background-color: #c5c5c5;
    }
  }

  .global-float-cont {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}

.custom-class {
  height: 100%;
  width: 100%;
  padding: 0;
}
</style>
